import {
  type PropsWithChildren,
  type ReactElement,
  createContext,
  useContext,
  useMemo,
  useState,
} from 'react';
import type { DialogContextType } from './types';

const DialogContext = createContext({} as DialogContextType);

const DialogProvider = ({ children }: PropsWithChildren): ReactElement => {
  const [dialogState, setDialogState] = useState<{
    open: boolean;
    component: ReactElement | null;
    state?: { [key: string]: string };
  }>({ open: false, component: null, state: {} });

  const showDialog = ({
    component,
    state = {},
  }: {
    component: ReactElement;
    state?: { [key: string]: string };
  }): void => {
    setDialogState({ open: true, component, state: state || {} });
  };

  const hideDialog = (): void => {
    setDialogState({ open: false, component: null, state: {} });
  };

  const value = useMemo(
    () => ({
      isOpen: dialogState.open,
      state: dialogState.state || {},
      showDialog,
      hideDialog,
    }),
    [dialogState]
  );

  return (
    <DialogContext.Provider value={value}>
      {children} {dialogState.open && dialogState.component}
    </DialogContext.Provider>
  );
};

const useDialogContext = (): DialogContextType => useContext(DialogContext);

export { DialogContext, DialogProvider, useDialogContext };
